import React, { useState, useEffect } from "react";
import {
  HiCloudArrowUp,
  HiUser,
  HiCircleStack,
  HiClock,
  HiArrowPath,
  HiQueueList,
  HiSignalSlash,
  HiChevronLeft,
} from "react-icons/hi2";
import peakIcon from "../assets/whiteIcon.svg";
import { useNavigate } from "react-router-dom";
import {
  useAppUpdates,
  useFeedbackSubmission,
  useFeedbackList,
  useFeedbackHelpers,
  usePendingFeedback,
  useOldFeedback,
} from "../hooks/useFeedbackData";
import Header from "../components/layout/Header";
const Updates: React.FC = () => {
  const [feedback, setFeedback] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const navigate = useNavigate();
  // Set a timeout for loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimeout(true);
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timer);
  }, []);

  // Use offline-first hooks following developer guidelines
  const {
    updates,
    latestUpdate,
    loading: updatesLoading,
    isFromCache: updatesFromCache,
    isOffline: updatesOffline,
  } = useAppUpdates();
  const {
    feedbackList,
    loading: feedbackLoading,
    isFromCache: feedbackFromCache,
    isOffline: feedbackOffline,
    fetchFeedbackData,
  } = useFeedbackList();
  const {
    submitFeedback,
    submitting,
    error: submissionError,
    isOffline: submitOffline,
  } = useFeedbackSubmission(async () => {
    // Fetch fresh data after successful submission to see the new feedback
    await fetchFeedbackData(false);
  });
  const { formatDate } = useFeedbackHelpers();

  // Old feedback from default database
  const {
    oldFeedbackList,
    loading: oldFeedbackLoading,
    isFromCache: oldFeedbackFromCache,
    isOffline: oldFeedbackOffline,
  } = useOldFeedback();

  // Pending feedback queue management
  const {
    pendingItems,
    addPendingItem,
    removePendingItem,
    syncPendingSubmissions,
    syncing,
    isOffline: pendingOffline,
  } = usePendingFeedback(async () => {
    // Refetch feedback data after successful sync
    await fetchFeedbackData(false);
  });

  // Debug logging
  console.log("[UPDATES_PAGE] State:", {
    updatesLoading,
    feedbackLoading,
    oldFeedbackLoading,
    updatesLength: updates.length,
    feedbackLength: feedbackList.length,
    oldFeedbackLength: oldFeedbackList.length,
    pendingLength: pendingItems.length,
    updatesFromCache,
    feedbackFromCache,
    oldFeedbackFromCache,
    updatesOffline,
    feedbackOffline,
    oldFeedbackOffline,
    syncing,
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!feedback.trim()) return;

    try {
      const result = await submitFeedback(
        feedback,
        selectedFile || undefined,
        addPendingItem
      );

      // Reset form on success or when queued
      if (result?.submitted || result?.queued) {
        setFeedback("");
        setSelectedFile(null);

        // Reset file input
        const fileInput = document.getElementById(
          "screenshot-upload"
        ) as HTMLInputElement;
        if (fileInput) fileInput.value = "";
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Error handling is managed by the hook
    }
  };

  // Show loading state for initial load only if all are loading and no data exists
  const isInitialLoading =
    updatesLoading &&
    feedbackLoading &&
    oldFeedbackLoading &&
    updates.length === 0 &&
    feedbackList.length === 0 &&
    oldFeedbackList.length === 0 &&
    !loadingTimeout;

  if (isInitialLoading) {
    return (
      <div className="container-app py-8 text-center">
        <div className="loading loading-spinner loading-lg"></div>
        <p className="text-sm text-base-content/70 mt-4">Loading feedback system...</p>
      </div>
    );
  }

  if (
    loadingTimeout &&
    (updatesLoading || feedbackLoading || oldFeedbackLoading) &&
    updates.length === 0 &&
    feedbackList.length === 0 &&
    oldFeedbackList.length === 0
  ) {
    return (
      <div className="container-app py-8 text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-4">
          Loading timeout
        </h2>
        <p className="text-sm text-base-content/70 mb-6">
          The feedback system is taking longer than expected to load. This might
          be because the documents don't exist yet.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-app px-8 sm:px-6 lg:px-8 py-6 sm:py-12 lg:py-24">
        <div className="flex flex-col gap-6 sm:gap-8 lg:gap-10 max-w-4xl mx-auto w-full">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:gap-4 mt-20">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/support")}
                className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
                aria-label="Back to support"
              >
                <HiChevronLeft className="h-6 w-6 text-base-content" />
              </button>
              <h1 className="text-3xl font-grotesk font-semibold ml-4">
                Public Feedback Board
              </h1>
            </div>
            <p className="text-lg font-grotesk text-base-content/80">
              We have closed the public submission board and are moving to a
              ticket support system to answer your questions and feedback more
              efficiently.
            </p>
            {/* Offline/Cache Status Indicators */}
            {(updatesOffline ||
              feedbackOffline ||
              oldFeedbackOffline ||
              submitOffline ||
              pendingOffline) && (
              <div className="alert alert-warning mb-3 sm:mb-4">
                <HiSignalSlash className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm">
                    You're currently offline. Your feedback will be queued and
                    submitted when you're back online.
                  </p>
                  {pendingItems.length > 0 && (
                    <p className="text-xs sm:text-sm">
                      ({pendingItems.length} pending)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Syncing Status */}
            {syncing && (
              <div className="alert alert-info mb-3 sm:mb-4">
                <HiArrowPath className="w-4 h-4 sm:w-5 sm:h-5 animate-spin flex-shrink-0" />
                <div className="w-full min-w-0">
                  <p className="text-sm mb-2">Syncing pending feedback...</p>
                  <div className="w-full bg-base-300 rounded-md h-2">
                    <div className="bg-blue-600 h-2 rounded-md animate-pulse w-1/2"></div>
                  </div>
                </div>
              </div>
            )}
            {/* Latest Updates Section */}
            {/* {latestUpdate && (
              <div className="rounded-lg bg-base-300 p-4 mt-4 mb-4">
                <div className="w-full">
                  <h4 className="font-semibold text-sm sm:text-base">
                    Latest Deployed Update
                  </h4>
                  <p className="text-xs sm:text-sm text-base-content/70 font-grotesk mt-1">
                    {formatDate(latestUpdate.createdAt)}
                  </p>
                  <p className="text-sm whitespace-pre-line mt-2 leading-relaxed">
                    {latestUpdate.message}
                  </p>
                </div>
              </div>
            )} */}

            {(updatesFromCache || feedbackFromCache || oldFeedbackFromCache) &&
              !(updatesOffline || feedbackOffline || oldFeedbackOffline) &&
              !syncing && (
                <div className="flex mb-3 sm:mb- bg-base-300 p-3 rounded-lg">
                  <HiCircleStack className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <div className="min-w-0 ml-2">
                    <p className="text-xs text-base-content/70">
                      Showing cached data.{" "}
                      {updatesLoading || feedbackLoading
                        ? "Updating..."
                        : "Data is up to date."}
                    </p>
                  </div>
                </div>
              )}

            {/* Feedback Form */}
            <div className="rounded-xl bg-base-100 shadow-lg p-8 mb-4 mt-4 sm:mb-6 border-2">
              <h2 className="font-grotesk text-xl">Common Requests</h2>

              <p className="font-bold mt-4 font-grotesk">
                Wearable Data Issues
              </p>
              <p>
                For wearable data issues, please submit a ticket in Help &
                Support with specific details about the error so we can try
                manually address. With so many wearable devices available and
                types of data synced, there can be mismatches which we can
                rectify manually.
              </p>
              <p className="font-bold mt-4 font-grotesk">Sport Requests</p>
              <p>
                If your sport is not available, please select the sport closest
                and add custom tags. Submit a ticket in the Help & Support
                section. We prioritise sports based on the amount of requests
                received.
              </p>
              <p className="font-bold mt-4 font-grotesk">Bugs & Glitches</p>
              <p>
                If you find a bug or glitch, please report this with a
                screenshot and how the error occured. This helps us kill bugs
                efficiently.
              </p>

              <button
                onClick={() => navigate("/support")}
                className="w-full mt-8 px-6 py-3 rounded-full font-medium transition-colors bg-base-300 hover:bg-base-content/40 text-base-content"
              >
                Submit Ticket
              </button>
              {/* <textarea
                className="textarea rounded-lg textarea-bordered w-full h-24 sm:h-32 mb-4 bg-base-200 text-sm sm:text-base resize-none"
                placeholder="Enter your feedback or glitches here..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={submitting}
              />

              <div className="mb-4">
                <input
                  accept="image/*"
                  className="hidden"
                  id="screenshot-upload"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={submitting}
                />
                <label htmlFor="screenshot-upload">
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-lg w-full px-4 py-3 bg-base-300 text-base-content/80 hover:bg-base-300 transition-colors text-sm sm:text-base"
                    disabled={submitting}
                    onClick={() =>
                      document.getElementById("screenshot-upload")?.click()
                    }
                  >
                    <HiCloudArrowUp className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>Upload Screenshot</span>
                  </button>
                </label>
                {selectedFile && (
                  <div className="flex items-center justify-between bg-base-200 rounded-lg p-3 mt-3 text-sm">
                    <div className="flex items-center min-w-0">
                      <span className="text-base-content/70 mr-2">📎</span>
                      <span className="text-base-content/80 truncate">
                        <strong>{selectedFile.name}</strong>
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 ml-3 flex-shrink-0 text-lg"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {submissionError && (
                <div className="alert alert-error mb-4 text-sm">
                  <p>{submissionError}</p>
                </div>
              )}

              <button
                className="w-full bg-neutral hover:bg-neutral/90 rounded-lg text-white py-3 sm:py-4 px-4 sm:px-6 font-bold font-grotesk text-sm sm:text-base transition-colors"
                onClick={handleSubmit}
                disabled={!feedback.trim() || submitting}
              >
                {submitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button> */}
            </div>
          </div>

          {/* Submissions Section */}
          <div className="flex flex-col gap-3 sm:gap-4 border-t pt-8">
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-xl font-bold font-grotesk">
                Past Submissions
              </h2>
              {(feedbackLoading || oldFeedbackLoading) &&
                (feedbackList.length > 0 || oldFeedbackList.length > 0) && (
                  <div className="loading loading-spinner loading-sm"></div>
                )}
            </div>

            <div className="card bg-gradient-to-br from-blue-50 to-base-100 shadow-sm border-2 border-dashed border-blue-300 p-4 sm:p-6">
              <p className="font-grotesk">
                Thank you everyone who provided feedback here since our launch.
                We were overwhelmed with all your messages and are very grateful
                for your support :) - Tori
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {/* Pending Feedback Items */}
              {pendingItems.map((item: any) => (
                <div
                  key={item.id}
                  className="card bg-gradient-to-br from-orange-50 to-base-100 shadow-sm border-2 border-dashed border-orange-300 p-4 sm:p-6"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <HiQueueList className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-orange-600">
                      Pending Submission
                    </span>
                    <div className="badge badge-warning badge-outline gap-1 text-xs">
                      {syncing ? (
                        <>
                          <HiArrowPath className="w-3 h-3 animate-spin" />
                          <span className="hidden sm:inline">Syncing...</span>
                          <span className="sm:hidden">Sync</span>
                        </>
                      ) : (
                        <>
                          <HiClock className="w-3 h-3" />
                          <span className="hidden sm:inline">
                            Waiting for connection
                          </span>
                          <span className="sm:hidden">Waiting</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <div className="avatar flex-shrink-0">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-orange-600 flex items-center justify-center">
                          <HiUser className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-orange-600">
                        Your feedback (not sent yet)
                      </span>
                      <span className="text-xs text-base-content/60 break-all sm:break-normal">
                        {item.createdAt.toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-sm mb-2 leading-relaxed">
                      {item.feedback}
                    </p>
                    {item.imageFile && (
                      <p className="text-xs text-base-content/70 mt-2 break-all">
                        📎 Image attached: {item.imageFile.name}
                      </p>
                    )}
                    {item.retryCount && item.retryCount > 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        Failed to send {item.retryCount} time(s). Will retry
                        when connection is stable.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      className="btn btn-outline btn-sm flex-1 sm:flex-none text-xs sm:text-sm"
                      onClick={() => syncPendingSubmissions()}
                      disabled={syncing || submitOffline || pendingOffline}
                    >
                      {syncing ? (
                        <>
                          <span className="loading loading-spinner loading-xs"></span>
                          <span className="hidden sm:inline">Syncing...</span>
                          <span className="sm:hidden">Sync</span>
                        </>
                      ) : (
                        <>
                          <HiArrowPath className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Retry Now</span>
                          <span className="sm:hidden">Retry</span>
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm text-red-600 flex-1 sm:flex-none text-xs sm:text-sm"
                      onClick={() => removePendingItem(item.id)}
                      disabled={syncing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}

              {/* Regular Feedback Items */}
              {feedbackList.map((item) => (
                <div
                  key={item.id}
                  className="card bg-base-100 shadow-sm p-4 sm:p-6"
                >
                  {/* User Feedback */}
                  <div className="mb-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <div className="avatar flex-shrink-0">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-base-content/40 !flex !items-center !justify-center">
                          <HiUser className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                      </div>
                      <span className="text-xs text-base-content/60 font-grotesk">
                        {item.adminResponse ? "Responded" : "Awaiting Response"}
                      </span>
                      <span className="text-xs text-base-content/60 break-all sm:break-normal">
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm mb-2 leading-relaxed">
                      {item.feedback}
                    </p>
                    {item.imageUrl && (
                      <div className="mt-2">
                        <img
                          src={item.imageUrl}
                          alt="Feedback screenshot"
                          className="max-w-full sm:max-w-[320px] max-h-[200px] sm:max-h-[320px] object-contain rounded-lg border border-base-300"
                        />
                      </div>
                    )}
                  </div>

                  {/* Admin Response */}
                  {item.adminResponse && (
                    <>
                      <div className="divider"></div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <div className="avatar flex-shrink-0">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-neutral !flex items-center justify-center">
                              <img
                                src={peakIcon}
                                alt="PEAK"
                                className="w-2.5 h-2.5 sm:w-3 sm:h-3 p-0.5 sm:p-1"
                              />
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-base-content font-grotesk">
                            PEAK^
                          </span>
                          {item.adminResponseAt && (
                            <span className="text-xs text-base-content/60 break-all sm:break-normal">
                              {formatDate(item.adminResponseAt)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-base-content font-medium leading-relaxed">
                          {item.adminResponse}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Old Feedback Items */}
              {oldFeedbackList.length > 0 && (
                <>
                  <div className="divider mt-6 sm:mt-8 mb-3 sm:mb-4">
                    <span className="text-sm text-base-content/60">
                      Previous Feedback
                    </span>
                  </div>
                  {oldFeedbackList.map((item) => (
                    <div
                      key={item.id}
                      className="card bg-base-200 shadow-sm p-4 sm:p-6 border border-base-300"
                    >
                      {/* User Feedback */}
                      <div className="mb-4">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <div className="avatar flex-shrink-0">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-base-content/40 !flex !items-center !justify-center">
                              <HiUser className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                            </div>
                          </div>
                          <span className="text-xs text-base-content/60 font-grotesk">
                            {item.response ? "Responded" : "Awaiting Response"}
                          </span>
                          <span className="text-xs text-base-content/60 break-all sm:break-normal">
                            {item.timestamp
                              .toDate()
                              .toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                          </span>
                          <span className="text-xs text-base-content/50 ml-auto hidden sm:inline">
                            (Legacy)
                          </span>
                        </div>
                        <p className="text-sm mb-2 leading-relaxed">
                          {item.message}
                        </p>
                        {item.imageUrl && (
                          <div className="mt-2">
                            <img
                              src={item.imageUrl}
                              alt="Feedback screenshot"
                              className="max-w-full sm:max-w-[320px] max-h-[200px] sm:max-h-[320px] object-contain rounded-lg border border-base-300"
                            />
                          </div>
                        )}
                      </div>

                      {/* Admin Response */}
                      {item.response && (
                        <>
                          <div className="divider"></div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <div className="avatar flex-shrink-0">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-neutral !flex items-center justify-center">
                                  <img
                                    src={peakIcon}
                                    alt="PEAK"
                                    className="w-2.5 h-2.5 sm:w-3 sm:h-3 p-0.5 sm:p-1"
                                  />
                                </div>
                              </div>
                              <span className="text-xs font-semibold text-base-content font-grotesk">
                                PEAK^
                              </span>
                            </div>
                            <p className="text-sm text-base-content font-medium leading-relaxed">
                              {item.response}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}

              {feedbackList.length === 0 &&
                pendingItems.length === 0 &&
                oldFeedbackList.length === 0 &&
                !feedbackLoading &&
                !oldFeedbackLoading && (
                  <div className="card bg-base-100 shadow-sm p-4 sm:p-6 text-center">
                    <p className="text-sm text-base-content/70 leading-relaxed">
                      No feedback submitted yet. Be the first to share your
                      thoughts!
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Updates;
